import React, { lazy } from 'react'
import { targetEnv } from '@fs/zion-config'
import { AuthRoute, NotFound, Redirect, Route, Switch } from '@fs/zion-router'

import { Subheader } from 'components'
import PermissionRoute from 'components/PermissionRoute/PermissionRoute'
import { useCommon } from 'hooks'
import useRoutes from 'hooks/useRoutes'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2, // retry twice before failing
      retryDelay: 2000, // wait 2 seconds between retries
      refetchOnWindowFocus: false, // don’t refetch when window regains focus
      refetchOnReconnect: true, // refetch on network reconnect (default behavior)
      refetchOnMount: true, // refetch when component remounts (default behavior)
    },
  },
})

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy

const GraphiQLPage = lazy(() => import('views/GraphiQLPage/GraphiQLPage'))

export default function App() {
  const { disableSubheader } = useCommon()
  const { routes } = useRoutes()

  return (
    <>
      {!disableSubheader && <Subheader />}

      <QueryClientProvider client={queryClient}>
        <Switch>
          {routes
            .filter(({ developmentOnly }) => !developmentOnly || process.env.NODE_ENV === 'development')
            .map(({ auth, Component, path, permissions, routeProps }) => {
              const RouteComponent = auth ? AuthRoute : Route
              /*
                The 'exact' prop is added to each route to avoid invalid URLs from getting
                directed to a valid route when possible. e.g. '/records/images/invalidRoute'
                would send the user to '/records/images' without the use of the 'exact' prop.
              */
              return (
                <RouteComponent key={path} exact {...routeProps} path={path}>
                  <PermissionRoute permissions={permissions}>
                    <Component />
                  </PermissionRoute>
                </RouteComponent>
              )
            })}
          {targetEnv === 'local' && <Route component={GraphiQLPage} path="/graphiql" />}
          {/* NOTE: This redirect handles a case where a Zion CMS page that redirects here can put the language code at the end of the path */}
          <Redirect from="/beta/:languageCode" to="/beta" />
          {/* 404 Page */}
          <Route component={NotFound} />
        </Switch>
      </QueryClientProvider>
    </>
  )
}
